import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  memo,
  type ReactNode,
} from "react";
import type { FlashCardUI } from "../models/FlashcardUI";
import { useFlashcardCarouselWeb } from "../hooks/useFlashcardCarouselWeb";
import carouselStyles from "./FlashcardCarousel.module.scss";

export type FlashcardCarouselHandle = {
  scrollToIndex: (index: number) => void;
  goNext: () => void;
  goPrevious: () => void;
};

type FlashcardCarouselProps = {
  flashcards: FlashCardUI[];
  slideWidth: number;
  currentIndex: number;
  totalCards: number;
  allowHorizontalUserScroll: boolean;
  initialScrollIndex?: number;
  onIndexChange: (index: number) => void;
  onReachedEnd?: () => void;
  renderItem: (item: FlashCardUI, index: number) => ReactNode;
};

const FlashcardCarouselInner = forwardRef<
  FlashcardCarouselHandle,
  FlashcardCarouselProps
>(function FlashcardCarouselInner(
  {
    flashcards,
    slideWidth,
    currentIndex,
    totalCards,
    initialScrollIndex,
    onIndexChange,
    onReachedEnd,
    renderItem,
    allowHorizontalUserScroll,
  },
  ref,
) {
  const resolvedSlideWidth = Math.max(slideWidth, 1);

  const {
    scrollerRef,
    scrollToIndex,
    navigateToNext,
    navigateToPrevious,
    keyExtractor,
    onScrollerScroll,
    onScrollEnd,
    onPointerDown,
    onPointerUp,
  } = useFlashcardCarouselWeb({
    currentIndex,
    totalCards,
    onIndexChange,
    onReachedEnd,
    initialScrollIndex,
    slideWidth: resolvedSlideWidth,
    controlledScrollSync: !allowHorizontalUserScroll,
  });

  useImperativeHandle(
    ref,
    () => ({
      scrollToIndex: (index: number) => scrollToIndex(index, "instant"),
      goNext: navigateToNext,
      goPrevious: navigateToPrevious,
    }),
    [scrollToIndex, navigateToNext, navigateToPrevious],
  );

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scrollend", onScrollEnd);
    return () => el.removeEventListener("scrollend", onScrollEnd);
  }, [onScrollEnd, scrollerRef]);

  return (
    <div
      className={carouselStyles.viewport}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    >
      <div
        ref={scrollerRef}
        className={`${carouselStyles.scroller} ${allowHorizontalUserScroll ? carouselStyles.scrollerMweb : carouselStyles.scrollerWeb}`}
        onScroll={onScrollerScroll}
      >
        {flashcards.map((item, index) => (
          <div
            key={keyExtractor(item, index)}
            className={carouselStyles.slide}
            style={{
              flex: `0 0 ${resolvedSlideWidth}px`,
              width: resolvedSlideWidth,
              minWidth: resolvedSlideWidth,
              maxWidth: resolvedSlideWidth,
            }}
          >
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    </div>
  );
});

export const FlashcardCarousel = memo(FlashcardCarouselInner);

export type { FlashcardCarouselProps };
